// TaskSection.tsx
import React, { useEffect, useState } from 'react';
import TaskIconRenderer from '../Utility/TaskIconRenderer';
import { PiX } from 'react-icons/pi';
import { MdDragIndicator } from 'react-icons/md';
import add_task_icon from '../../../assets/icons/add_task.svg';
import startendbar from '../../../assets/icons/editclienttask.svg';
import { IconTrash } from '@tabler/icons-react';
import AddTaskDialog, {
  Task,
} from '../../../reusable/Client/dialog/AddTaskDialog';
import RemoveTaskDialog from '../../../reusable/Client/dialog/RemoveTaskDialog';
import { useTranslation } from 'react-i18next';

interface Props {
  /** ADM-22。確認文言に出す配送先名。以前は "ABC" 固定だった。 */
  clientName?: string;
  selectedTasks: Task[];
  setSelectedTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  isRemoveTask?: boolean;
  showErrorTooltip: boolean;
  setShowErrorTooltip: (errorTooltip: boolean) => void;
  /**
   * RV-129。保存（作成）を送っている間は作業の追加・削除・並べ替えをさせない。送った本文と
   * 画面の内容が食い違うと、結果が分からなかった作成の送り直しが別の内容になる。
   */
  disabled?: boolean;
}

const TaskAssignment: React.FC<Props> = ({
  clientName = '',
  selectedTasks,
  setSelectedTasks,
  isRemoveTask,
  showErrorTooltip,
  setShowErrorTooltip,
  disabled = false,
}) => {
  const { t } = useTranslation('client');
  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isRemoveTaskModalOpen, setIsRemoveTaskModalOpen] = useState(false);
  const [selectedTaskIdForRemoval, setSelectedTaskIdForRemoval] = useState<
    number | null
  >(null);
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null);

  // RV-129。送信が始まったら、開いている追加・削除のダイアログと削除モード・ドラッグを閉じる
  // （送信の後に古い操作の続きが出てこないように）。
  useEffect(() => {
    if (!disabled) return;
    setIsTaskModalOpen(false);
    setIsRemoveTaskModalOpen(false);
    setIsRemoveMode(false);
    setDraggedTaskIndex(null);
  }, [disabled]);

  // Handle task addition
  // PR #258 の Codex の P1（FR-AC8）。ダイアログが返した作業そのものを使う。以前は ID から親の一覧
  // （別に読んだもの）で引き直し、親の一覧の取得が失敗・古いと、選んだ作業が黙って捨てられた。
  // そのため親の一覧の取得はやめた（候補はダイアログが読んで見せる）。
  const handleAddTasks = (chosenTasks: Task[]) => {
    if (disabled) return;
    setSelectedTasks((prevSelectedTasks) => {
      const chosenIds = new Set(chosenTasks.map((task) => task.id));
      const existingTaskIds = new Set(prevSelectedTasks.map((task) => task.id));

      // 既に選んでいて、今も選ばれている作業はそのまま（並びも保つ）。
      const tasksToKeep = prevSelectedTasks.filter((task) => chosenIds.has(task.id));

      // 新しく選ばれた作業を後ろに足す。
      const newTasks = chosenTasks
        .filter((task) => !existingTaskIds.has(task.id))
        .map((task) => ({ ...task, isDisabled: false }));

      return [...tasksToKeep, ...newTasks];
    });

    setIsTaskModalOpen(false);
  };

  // Handle task removal
  const handleRemoveTask = () => {
    if (selectedTaskIdForRemoval !== null && !disabled) {
      setSelectedTasks((prevSelected) =>
        prevSelected.filter((task) => task.id !== selectedTaskIdForRemoval)
      );
    }
    setIsRemoveTaskModalOpen(false);
  };

  // Handle dragging of tasks
  const onDragStart = (index: number) => {
    if (disabled) return;
    setDraggedTaskIndex(index);
  };

  const onDragEnter = (index: number) => {
    if (!disabled && draggedTaskIndex !== null && draggedTaskIndex !== index) {
      const updatedTasks = [...selectedTasks];
      const [draggedTask] = updatedTasks.splice(draggedTaskIndex, 1);
      updatedTasks.splice(index, 0, draggedTask);
      setSelectedTasks(updatedTasks);
      setDraggedTaskIndex(index);
    }
  };

  const onDragEnd = () => {
    setDraggedTaskIndex(null);
  };

  return (
    <>
      <div className="flex flex-col px-6 py-4 gap-4">
        <div className="flex flex-row justify-between mb-4">
          <div className="relative">
            <h6
              className="font-medium text-subtitle-1 relative inline-block cursor-pointer"
              onMouseEnter={() => setShowErrorTooltip(true)}
              onMouseLeave={() => setShowErrorTooltip(false)}
            >
              {t('detail.tasks')} <span className="text-error">*</span>
            </h6>
            {showErrorTooltip && (
              <div className="absolute bg-red-100 text-red-600 border border-red-300 p-2 rounded mt-1 z-10 whitespace-nowrap">
                {t('manage_client.add_task_required')}
              </div>
            )}
          </div>
          <div className="flex flex-row gap-6 text-primary/300 items-center">
            {!isRemoveMode ? (
              <>
                <button
                  type="button"
                  className="h-8 bg-primary/50 text-primary/300 px-5 rounded-md text-caption disabled:opacity-50"
                  disabled={disabled}
                  onClick={() => setIsTaskModalOpen(true)}
                >
                  + {t('manage_client.add_task')}
                </button>
                {isRemoveTask && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setIsRemoveMode(true)} // Activate remove mode
                    className="h-8 flex flex-row gap-1 items-center text-primary/300 rounded-md text-caption"
                  >
                    <IconTrash stroke={1.5} size={16} />
                    {t('manage_client.remove_task')}
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setIsRemoveMode(false)} // Deactivate remove mode
                className="h-8 text-primary/300 rounded-md text-caption"
              >
                {t('action.cancel')}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-2 w-full justify-between items-center mb-2">
          <p className="flex-shrink-0 text-primary/75">{t('ui.task-start')}</p>
          <img src={startendbar} alt="" className="flex-grow" />
          <p className="flex-shrink-0 text-primary/75">{t('ui.task-end')}</p>
        </div>
        <div className="flex flex-row overflow-x-auto gap-4 pb-4">
          {selectedTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center w-full h-36 gap-3">
              <img src={add_task_icon} alt="" className="h-12" />
              <p className="text-subtitle-2 font-medium">
                {t('manage_client.add_task_title')}
              </p>
              <p className="text-caption text-neutral/100">
                {t('manage_client.add_task_subtitle')}
              </p>
            </div>
          ) : (
            selectedTasks.map((task, index) => (
              <div
                key={task.id}
                data-task-card
                className={`relative flex flex-col items-center border-r last:border-none pt-2 pb-6 gap-4 ${
                  disabled ? '' : 'cursor-move'
                }`}
                draggable={!disabled}
                onDragStart={() => onDragStart(index)}
                onDragEnter={() => onDragEnter(index)}
                onDragEnd={onDragEnd}
              >
                <div className="w-40 flex flex-col justify-center items-center">
                  <TaskIconRenderer iconName={task.icon} />
                  <p className="truncate">{task.name}</p>
                </div>

                {!isRemoveMode ? (
                  <MdDragIndicator
                    size={24}
                    className="transform rotate-90 text-neutral/100"
                  />
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setSelectedTaskIdForRemoval(task.id);
                      setIsRemoveTaskModalOpen(true);
                    }}
                  >
                    <PiX size={24} className="text-neutral/100" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Task Selection Modal */}
      {/* RV-129。送信中は作業の追加・削除のダイアログを出さない（開いていても閉じる）。 */}
      {isTaskModalOpen && !disabled && (
        <AddTaskDialog
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          tasks={selectedTasks}
          onAddTasks={handleAddTasks}
        />
      )}

      {isRemoveTaskModalOpen && !disabled && (
        <RemoveTaskDialog
          clientName={clientName}
          isOpen={isRemoveTaskModalOpen}
          onClose={() => setIsRemoveTaskModalOpen(false)}
          onDelete={handleRemoveTask}
          taskId={selectedTaskIdForRemoval}
        />
      )}
    </>
  );
};

export default TaskAssignment;
